import {useMemo} from 'react';

import {apiClient} from 'common/util/api-client';
import type {FeatureFlagApiPayload, FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';

interface UseFeatureFlagsApiProps {
  projectId: string | number;
}

type ListFeatureFlagsResponse = FeatureFlagApiResponse[];
type GetFeatureFlagRequest = {id: string};
type CreateFeatureFlagRequest = FeatureFlagApiPayload;
type UpdateFeatureFlagRequest = FeatureFlagApiPayload & {id: string};
type DeleteFeatureFlagRequest = {id: string};
type DuplicateFeatureFlagRequest = {id: string};

const DEFAULT_EXPERIMENT_VARIANT_NAME = `test`;
const DEFAULT_CONTROL_VARIANT_NAME = `control`;

export const buildNewFeatureFlag = (): FeatureFlagApiPayload => ({
  name: `New Feature Flag`,
  key: `new_feature_flag_${Date.now()}`,
  context: `distinct_id`,
  serving_method: `client`,
  ruleset: {
    variants: [
      {
        key: DEFAULT_CONTROL_VARIANT_NAME,
        value: DEFAULT_CONTROL_VARIANT_NAME,
        description: ``,
        split: 0.5,
        is_control: true,
        is_sticky: false,
      },
      {
        key: DEFAULT_EXPERIMENT_VARIANT_NAME,
        value: DEFAULT_EXPERIMENT_VARIANT_NAME,
        description: ``,
        split: 0.5,
        is_control: false,
        is_sticky: false,
      },
    ],
    rollout: [
      {
        name: `All Users`,
        rollout_percentage: 0,
      },
    ],
  },
  tags: [],
});

export function featureFlagsApi(props: UseFeatureFlagsApiProps) {
  const basePath = `/api/app/projects/${props.projectId}/feature-flags`;
  return {
    listFeatureFlags: () => apiClient.get<ListFeatureFlagsResponse>(basePath),

    getFeatureFlag: ({id}: GetFeatureFlagRequest) => {
      return apiClient.get<FeatureFlagApiResponse>(`${basePath}/${id}`);
    },

    createFeatureFlag: (flag: CreateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(basePath, flag);
    },

    updateFeatureFlag: ({id, ...flag}: UpdateFeatureFlagRequest) => {
      return apiClient.put<FeatureFlagApiResponse>(`${basePath}/${id}`, flag);
    },

    deleteFeatureFlag: ({id}: DeleteFeatureFlagRequest) => {
      return apiClient.delete(`${basePath}/${id}`);
    },

    duplicateFeatureFlag: ({id}: DuplicateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/duplicate`, {});
    },
  } as const;
}

export function useFeatureFlagsApi({projectId}: UseFeatureFlagsApiProps) {
  const api = useMemo(() => featureFlagsApi({projectId}), [projectId]);
  return api;
}
